import { useEffect, useState } from "react";
import { Guildhall } from "./Guildhall";
import { App } from "../app/App";
import { api, ApiError, gamePath, type Game, type Guest } from "./api";
import { takeCredential } from "../session/fragment";
import { IssueContextProvider } from "../issues/IssueContext";
import { IssueReporter } from "../issues/IssueReporter";
import "../styles/clubhouse.css";

type IdentityReply = {
    mode: string;
    identity: Guest | null;
    recoveryToken?: string;
};
const navigate = (path: string) => {
    window.location.href = path;
};
const errorMessage = (error: unknown) =>
    error instanceof Error
        ? error.message
        : "Connection interrupted. Please try again.";
// Capture once before React's StrictMode renders. Consuming a fragment in render
// would discard its capability during the development double render.
const inboundRecovery =
    typeof window === "undefined"
        ? null
        : new URLSearchParams(window.location.hash.slice(1)).get("recover");
const inboundCredential =
    typeof window === "undefined" || !window.location.pathname.endsWith("/play")
        ? null
        : takeCredential(window.location, window.history, undefined, false);
if (inboundRecovery)
    // Keep the query: `?ui=<layout>` and `?seat=` survive recovery (the fragment is the only part that goes).
    window.history.replaceState(null, "", window.location.pathname + window.location.search);

export function ClubhouseRouter() {
    const [mode, setMode] = useState<
        "loading" | "legacy" | "rooms" | "offline"
    >("loading");
    const [guest, setGuest] = useState<Guest | null>(null);
    const [recovery, setRecovery] = useState<string | null>(null);
    useEffect(() => {
        let cancelled = false;
        const recoveryToken = inboundRecovery;
        const load = async () => {
            try {
                let reply = recoveryToken
                    ? await api<IdentityReply>("/session/recover", {
                          token: recoveryToken,
                      })
                    : await api<IdentityReply>("/session");
                if (!reply.identity)
                    reply = await api<IdentityReply>("/session", {
                        name: "Guest",
                    });
                if (!cancelled) {
                    setGuest(reply.identity);
                    setRecovery(reply.recoveryToken ?? null);
                    setMode("rooms");
                }
            } catch (error) {
                if (!cancelled)
                    setMode(
                        error instanceof ApiError &&
                            error.status === 404 &&
                            !window.location.pathname.startsWith("/rooms")
                            ? "legacy"
                            : "offline",
                    );
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, []);
    if (mode === "legacy") return <App />;
    if (mode === "loading")
        return (
            <main className="club-loading">
                <span className="club-mark">◈</span>
                <p>Opening the clubhouse…</p>
            </main>
        );
    if (mode === "offline")
        return (
            <main className="club-loading">
                <h1>The clubhouse is offline</h1>
                <p>Your saved games will be here when the host returns.</p>
                <button
                    className="club-btn"
                    onClick={() => window.location.reload()}
                >
                    Try again
                </button>
            </main>
        );
    return (
        <IssueContextProvider>
            <Guildhall
                guest={guest}
                onGuest={setGuest}
                recovery={recovery}
                onRecovery={setRecovery}
                initialCredential={inboundCredential}
            />
        </IssueContextProvider>
    );
}

export function PlayerGate({
    game,
    spectator,
    fragment,
}: {
    game: Game;
    spectator: boolean;
    fragment: ReturnType<typeof takeCredential>;
}) {
    const selectedSeat = new URLSearchParams(window.location.search).get(
        "seat",
    );
    const [ready, setReady] = useState(spectator);
    const [credential, setCredential] =
        useState<ReturnType<typeof takeCredential>>(null);
    const [error, setError] = useState<string | null>(null);
    useEffect(() => {
        if (spectator) return;
        setError(null);
        setReady(false);
        let cancelled = false;
        const path = gamePath(game.roomId, game.id);
        void (async () => {
            try {
                const credential = fragment
                    ? await api<{ seat: string; token: string }>(
                          `${path}/player-link/exchange`,
                          { token: fragment.token },
                      )
                    : selectedSeat
                      ? await api<{ seat: string; token: string }>(
                            `${path}/seats/${encodeURIComponent(selectedSeat)}/join`,
                            {},
                        )
                      : await api<{ seat: string; token: string }>(
                            `${path}/player-link`,
                        );
                if (!cancelled) {
                    setCredential(credential);
                    setReady(true);
                }
            } catch (err) {
                if (!cancelled) setError(errorMessage(err));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [game.id, spectator, fragment?.token, selectedSeat]);
    if (error)
        return (
            <main className="club-loading" role="alert">
                <h1>Couldn’t open your player</h1>
                <p>{error}</p>
                <a className="club-btn" href={gamePath(game.roomId, game.id)}>
                    Return to the table
                </a>
            </main>
        );
    if (!ready)
        return (
            <main className="club-loading">
                <p>Opening your own seat…</p>
            </main>
        );
    return (
        <App
            spectator={spectator}
            credential={credential}
            paused={game.status === "paused" || game.archived}
        />
    );
}
